import React, { useCallback, useEffect, useMemo, useState } from 'react';

import ConfirmBottomSheet from 'components/ConfirmBottomSheet';
import Section from 'components/Section';
import { fetchAllAirlines } from 'actions/airlineException';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { Airline } from 'models/airlineException';
import { PaymentExceptionRequest, PaymentExceptionResponse } from 'models/paymentException';
import {
  createPaymentException,
  deletePaymentException,
  getAllPaymentExceptions,
  updatePaymentException,
} from 'services/paymentExceptionService';
import { extractErrorMessage } from 'utils/generic';

import PaymentExceptionForm from './Form';
import {
  Wrapper,
  HelperText,
  AddButton,
  AddButtonContent,
  AddButtonIcon,
  AddButtonLabel,
  Chevron,
  ExceptionsList,
  ExceptionItem,
  ExceptionSummary,
  ExceptionActions,
  IconButton,
} from './styles';

interface Props {
  companyId: string;
  lineOfCreditAvailable: boolean;
}

const PaymentExceptions = ({ companyId, lineOfCreditAvailable }: Props) => {
  const { t } = useStoreTranslation();
  const toast = useToast();

  const [exceptions, setExceptions] = useState<PaymentExceptionResponse[]>([]);
  const [airlines, setAirlines] = useState<Airline[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<PaymentExceptionResponse | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadExceptions = useCallback(async () => {
    try {
      const data = await getAllPaymentExceptions(companyId);
      setExceptions(data);
    } catch (error) {
      toast.add({ type: 'danger', message: extractErrorMessage(error) || t('defaultError.default') });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    loadExceptions();
    fetchAllAirlines()
      .then((response) => setAirlines(response.data.data))
      .catch(() => {});
  }, [companyId, loadExceptions]);

  const airlineNameByCode = useMemo(() => {
    const map: Record<string, string> = {};
    airlines.forEach((airline) => {
      map[airline.id] = airline.name;
    });
    return map;
  }, [airlines]);

  const formatSummary = (exception: PaymentExceptionResponse) => {
    const parts: string[] = [t(`paymentException.products.${exception.product}`)];

    const providerList = exception.provider
      ? exception.provider.split(',').map((p) => p.trim()).filter(Boolean)
      : [];
    if (providerList.length > 0) parts.push(providerList.join(' / '));

    const airlineList = exception.airlines || [];
    if (airlineList.length > 0) {
      parts.push(airlineList.map((code) => airlineNameByCode[code] || code).join(' / '));
    }

    parts.push(
      exception.creditCardByUser
        ? t('paymentException.form.creditCardByUserTitle')
        : t('paymentException.form.creditCardByCompanyTitle'),
    );

    return parts.join(' • ');
  };

  const handleOpenAdd = () => {
    setEditing(null);
    setShowForm(true);
  };

  const handleOpenEdit = (exception: PaymentExceptionResponse) => {
    setEditing(exception);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditing(null);
  };

  const handleSubmit = async (data: PaymentExceptionRequest) => {
    try {
      if (editing) {
        await updatePaymentException(editing.id, data);
        toast.add({ type: 'success', message: t('paymentException.successUpdate') });
      } else {
        await createPaymentException(data);
        toast.add({ type: 'success', message: t('paymentException.successCreate') });
      }
      handleCloseForm();
      await loadExceptions();
    } catch (error) {
      toast.add({ type: 'danger', message: extractErrorMessage(error) || t('defaultError.default') });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingId) return;
    try {
      setDeleteLoading(true);
      await deletePaymentException(deletingId);
      toast.add({ type: 'success', message: t('paymentException.successDelete') });
      setDeletingId(null);
      await loadExceptions();
    } catch (error) {
      toast.add({ type: 'danger', message: extractErrorMessage(error) || t('defaultError.default') });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Section title={t('paymentException.sectionTitle')}>
      <Wrapper>
        <HelperText>{t('paymentException.helperText')}</HelperText>
        <AddButton type="button" onClick={handleOpenAdd}>
          <AddButtonContent>
            <AddButtonIcon>
              <img src="/corporate/images/payment/cc/payment.svg" alt="payment" />
            </AddButtonIcon>
            <AddButtonLabel>{t('paymentException.addButton')}</AddButtonLabel>
          </AddButtonContent>
          <Chevron src="/corporate/images/chevron-right.svg" alt="open" />
        </AddButton>

        {exceptions.length > 0 && (
          <ExceptionsList>
            {exceptions.map((exception) => (
              <ExceptionItem key={exception.id}>
                <ExceptionSummary>{formatSummary(exception)}</ExceptionSummary>
                <ExceptionActions>
                  <IconButton
                    type="button"
                    onClick={() => handleOpenEdit(exception)}
                    aria-label={t('paymentException.actions.edit')}
                  >
                    <img src="/corporate/images/edit-new.svg" alt="edit" />
                  </IconButton>
                  <IconButton
                    type="button"
                    onClick={() => setDeletingId(exception.id)}
                    aria-label={t('paymentException.actions.delete')}
                  >
                    <img src="/corporate/images/trash.svg" alt="delete" />
                  </IconButton>
                </ExceptionActions>
              </ExceptionItem>
            ))}
          </ExceptionsList>
        )}
      </Wrapper>

      <PaymentExceptionForm
        show={showForm}
        companyId={companyId}
        lineOfCreditAvailable={lineOfCreditAvailable}
        initialValue={editing}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
      />

      <ConfirmBottomSheet
        show={Boolean(deletingId)}
        title={t('paymentException.deleteTitle')}
        content={t('paymentException.deleteMessage')}
        confirmText={t('paymentException.deleteConfirm')}
        red
        confirmLoading={deleteLoading}
        onClose={() => setDeletingId(null)}
        onConfirm={handleConfirmDelete}
      />
    </Section>
  );
};

export default PaymentExceptions;
